import React, { useMemo } from "react";

/**
 * Sparkline — 14-point line + soft area gradient, with a glowing last-point
 * dot (solid + 4px halo). Pure SVG; faithful to design_reference/primitives.jsx
 * (rolling our own keeps the design's pixel specs without recharts overhead
 * for what is effectively a tiny static visualization).
 *
 * Props:
 *  - data:   number[]   required, length >= 2
 *  - color:  string     hex/css color, defaults to indigo-400
 *  - width:  number     default 88
 *  - height: number     default 36
 *  - fill:   boolean    render the soft area fill (default true)
 *
 * Each Sparkline gets its own gradient `<defs>` id so multiple sparklines
 * with different colors can render on the same page without ID collision.
 */
const Sparkline = ({
  data,
  color = "#818cf8",
  width = 88,
  height = 36,
  fill = true,
}) => {
  const { line, area, last, id } = useMemo(() => {
    if (!data || data.length < 2) {
      return { line: "", area: "", last: [0, 0], id: "sg-empty" };
    }
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = Math.max(1, max - min);
    const stepX = width / (data.length - 1);
    const pts = data.map((d, i) => [
      i * stepX,
      height - 2 - ((d - min) / range) * (height - 4),
    ]);
    const linePath = pts
      .map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`)
      .join(" ");
    const areaPath = `${linePath} L${width} ${height} L0 ${height} Z`;
    return {
      line: linePath,
      area: areaPath,
      last: pts[pts.length - 1],
      id: "sg-" + Math.random().toString(36).slice(2, 7),
    };
  }, [data, width, height]);

  if (!data || data.length < 2) {
    return <svg width={width} height={height} aria-hidden="true" />;
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="overflow-visible"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {fill && <path d={area} fill={`url(#${id})`} />}
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={last[0]} cy={last[1]} r="2.5" fill={color} />
      <circle cx={last[0]} cy={last[1]} r="4.5" fill={color} fillOpacity="0.2" />
    </svg>
  );
};

export default Sparkline;
